import React, { useState, useEffect } from 'react';
import { FaExternalLinkAlt, FaCertificate } from 'react-icons/fa';

const AgentCard = ({ agentId, themeColors, onViewProfile }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch agent data
  useEffect(() => {
    const fetchAgentData = async () => {
      if (!agentId) return;
      
      try {
        setLoading(true);
        const response = await fetch(`https://app.akiya2.com/agents/get-agent-by-id/${agentId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch agent data');
        }
        
        const agentData = await response.json();
        setAgent(agentData);
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [agentId]);

  // Handle view profile click
  const handleViewProfile = () => {
    if (onViewProfile) {
      onViewProfile(agentId);
    } else {
      window.open(`/agent/${agentId}`, '_blank');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
        <div
          className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
          style={{ backgroundColor: themeColors.primary }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Property Agent
        </div>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gray-300 rounded-full flex-shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2"></div>
              </div>
              <div className="h-10 bg-gray-300 rounded w-28"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !agent) {
    return null;
  }

  // Get agent avatar using actual profile image
  const getAgentAvatar = () => {
    if (agent.image_url && agent.image_url.trim() !== '') {
      return agent.image_url;
    }
    // Generate a default avatar based on name if no profile image
    const initials = agent.name 
      ? agent.name.split(' ').map(n => n[0]).join('').toUpperCase()
      : 'A';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name || 'Agent')}&size=128&background=${themeColors.primary.replace('#', '')}&color=ffffff&bold=true`;
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
      {/* Header */}
      <div 
        className="px-6 py-4 text-white text-xl font-semibold flex items-center gap-3"
        style={{ backgroundColor: themeColors.primary }}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        Property Agent
      </div>

      {/* Simple Agent Content */}
      <div className="p-6 lg:p-8">
        <div className="flex items-center gap-4">
          
          {/* Agent Avatar & Basic Info */}
          <div className="relative flex-shrink-0">
            <img
              src={getAgentAvatar()}
              alt={agent.name || 'Agent'}
              className="w-16 h-16 rounded-full border-3 border-white shadow-md object-cover"
              onError={(e) => {
                const initials = agent.name 
                  ? agent.name.split(' ').map(n => n[0]).join('').toUpperCase()
                  : 'A';
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name || 'Agent')}&size=128&background=${themeColors.primary.replace('#', '')}&color=ffffff&bold=true`;
              }}
            />
            {agent.is_verified && (
              <div 
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white"
                style={{ backgroundColor: themeColors.primary }}
              >
                <FaCertificate className="w-2.5 h-2.5 text-white" />
              </div>
            )}
          </div>
          
          {/* Agent Details */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {agent.name || 'Agent'}
            </h3>
            <p className="text-sm text-gray-600 truncate">
              {agent.company || 'Real Estate Professional'}
            </p>
            {agent.years_experience && (
              <p className="text-xs text-gray-500 mt-1">
                {agent.years_experience} years experience
              </p>
            )}
          </div>

          {/* View Profile Button */}
          <button
            onClick={handleViewProfile}
            className="flex items-center gap-2 px-4 py-2 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md whitespace-nowrap"
            style={{ backgroundColor: themeColors.primary }}
          >
            <span>View Profile</span>
            <FaExternalLinkAlt className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentCard;